class IndexedDBService {
  constructor() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      throw new Error('IndexedDBService can only be used in a browser environment');
    }
    
    this.dbName = 'waiter-admin-db';
    this.dbVersion = 1;
    this.db = null;
    this.initPromise = null;
  }

  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores for different data types
        if (!db.objectStoreNames.contains('userData')) {
          const userStore = db.createObjectStore('userData', { keyPath: 'key' });
          userStore.createIndex('key', 'key', { unique: true });
        }

        if (!db.objectStoreNames.contains('orders')) {
          const ordersStore = db.createObjectStore('orders', { keyPath: 'key' });
          ordersStore.createIndex('key', 'key', { unique: true });
        }

        if (!db.objectStoreNames.contains('settings')) {
          const settingsStore = db.createObjectStore('settings', { keyPath: 'key' });
          settingsStore.createIndex('key', 'key', { unique: true });
        }

        if (!db.objectStoreNames.contains('notifications')) {
          const notificationsStore = db.createObjectStore('notifications', { keyPath: 'key' });
          notificationsStore.createIndex('key', 'key', { unique: true });
        }
      };
    });

    return this.initPromise;
  }

  async get(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
    });
  }

  async set(storeName, key, value) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ key, value });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async remove(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getAll(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const data = {};
        results.forEach(item => {
          data[item.key] = item.value;
        });
        resolve(data);
      };
    });
  }

  // Convenience methods for specific data types
  async getUserData(key) {
    return this.get('userData', key);
  }

  async setUserData(key, value) {
    return this.set('userData', key, value);
  }

  async getOrderData(key) {
    return this.get('orders', key);
  }

  async setOrderData(key, value) {
    return this.set('orders', key, value);
  }

  async getSetting(key) {
    return this.get('settings', key);
  }

  async setSetting(key, value) {
    return this.set('settings', key, value);
  }

  async removeSetting(key) {
    return this.remove('settings', key);
  }

  async getNotificationData(key) {
    return this.get('notifications', key);
  }

  async setNotificationData(key, value) {
    return this.set('notifications', key, value);
  }

  // Migration helper from localStorage
  async migrateFromLocalStorage() {
    try {
      const keys = Object.keys(localStorage);
      const migrationPromises = keys.map(async (key) => {
        const value = localStorage.getItem(key);
        let parsedValue;
        
        try {
          parsedValue = JSON.parse(value);
        } catch {
          parsedValue = value; // Keep as string if not JSON
        }

        // Determine which store to use based on key
        if (key === 'userDetails' || key.includes('Token')) {
          await this.setUserData(key, parsedValue);
        } else if (key === 'activeOrders' || key === 'checkoutDishes') {
          await this.setOrderData(key, parsedValue);
        } else if (key === 'isTerminal' || key === 'language' || key === 'locationGranted' || key === 'Gotovo') {
          await this.setSetting(key, parsedValue);
        } else {
          await this.setUserData(key, parsedValue); // Default to userData
        }
      });

      await Promise.all(migrationPromises);
      console.log('Migration from localStorage completed successfully');
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }

  // Check if IndexedDB is supported
  static isSupported() {
    return typeof window !== 'undefined' && 'indexedDB' in window && window.indexedDB;
  }

  // Fallback to localStorage if IndexedDB is not available
  static getFallbackStorage() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      console.warn('Not in browser environment, returning mock storage');
      return {
        getUserData: () => Promise.resolve(null),
        setUserData: () => Promise.resolve(),
        getOrderData: () => Promise.resolve(null),
        setOrderData: () => Promise.resolve(),
        getSetting: () => Promise.resolve(null),
        setSetting: () => Promise.resolve(),
        removeSetting: () => Promise.resolve(),
        getNotificationData: () => Promise.resolve(null),
        setNotificationData: () => Promise.resolve(),
        remove: () => Promise.resolve(),
        clear: () => Promise.resolve(),
        migrateFromLocalStorage: () => Promise.resolve(),
        init: () => Promise.resolve()
      };
    }
    
    if (this.isSupported()) {
      return new IndexedDBService();
    } else {
      console.warn('IndexedDB not supported, falling back to localStorage');
      return {
        getUserData: (key) => {
          const value = localStorage.getItem(key);
          try {
            return Promise.resolve(JSON.parse(value));
          } catch {
            return Promise.resolve(value);
          }
        },
        setUserData: (key, value) => {
          localStorage.setItem(key, JSON.stringify(value));
          return Promise.resolve();
        },
        getOrderData: (key) => {
          const value = localStorage.getItem(key);
          try {
            return Promise.resolve(JSON.parse(value));
          } catch {
            return Promise.resolve(value);
          }
        },
        setOrderData: (key, value) => {
          localStorage.setItem(key, JSON.stringify(value));
          return Promise.resolve();
        },
        getSetting: (key) => {
          const value = localStorage.getItem(key);
          try {
            return Promise.resolve(JSON.parse(value));
          } catch {
            return Promise.resolve(value);
          }
        },
        setSetting: (key, value) => {
          localStorage.setItem(key, JSON.stringify(value));
          return Promise.resolve();
        },
        removeSetting: (key) => {
          localStorage.removeItem(key);
          return Promise.resolve();
        },
        getNotificationData: (key) => {
          const value = localStorage.getItem(key);
          try {
            return Promise.resolve(JSON.parse(value));
          } catch {
            return Promise.resolve(value);
          }
        },
        setNotificationData: (key, value) => {
          localStorage.setItem(key, JSON.stringify(value));
          return Promise.resolve();
        },
        remove: (storeName, key) => {
          localStorage.removeItem(key);
          return Promise.resolve();
        },
        clear: (storeName) => {
          localStorage.clear();
          return Promise.resolve();
        },
        migrateFromLocalStorage: () => Promise.resolve(),
        init: () => Promise.resolve()
      };
    }
  }
}

// Create singleton instance
const indexedDBService = IndexedDBService.getFallbackStorage();

export default indexedDBService; 